import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  createTicketModalActions,
  selectCreateTicketModalData,
  selectCreateTicketModalStatus,
} from '../redux/slicers/createTicketModalSlicer';
import BigButton from './BigButton';
import * as Yup from 'yup';
import { Formik, useFormik } from 'formik';
import cx from 'classnames';
import { apiGetSupportPipelines, apiPostCreateTicket } from '../helpers/api';
import { convertToRaw, EditorState } from 'draft-js';
import { apiErrorHandler } from '../helpers/axiosHelper';
import { errorToast, successToast } from '../helpers/toastHelper';

import { icons } from '../assets';
import StyledSelect from './StyledSelect';
import { generateUUID } from '../helpers/jsHelper';
import RichEditor from './RichEditor/RichEditor';
import { useUserContext } from '../contexts/UserContext';
import { ticketsActions } from '../redux/slicers/ticketSlicer';
import { STATUS_NEW_ID } from '../constants/supportConstants';
import { usePrevious } from 'react-use';
import { useCustomKeyPress } from '../hooks/useCustomKeyPress';

const { CloseIconSmall, ExclamationIcon } = icons;

export function checkIfFilesAreTooBig(files) {
  let valid = true;
  if (files) {
    files.map((file) => {
      const size = file.size / 1024 / 1024;
      if (size > 5) {
        valid = false;
      }
    });
  }
  return valid;
}

const CreateTicketModal = () => {
  const dispatch = useDispatch();

  const open = useSelector(selectCreateTicketModalStatus);
  const ticketData = useSelector(selectCreateTicketModalData);
  const { currentUser } = useUserContext();
  const currentUserId = usePrevious(currentUser?.id);

  const [pipelineOptions, setPipelineOptions] = useState([]);

  const formik = useFormik({
    initialValues: {
      title: '',
      pipeline: '',
      description: EditorState.createEmpty(),
      attachments: [],
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .required('Please add a title')
        .max(200, 'Title must be less than 200 characters'),
      pipeline: Yup.object().required('Please select a category'),
      description: Yup.object()
        .test('has text', 'Please add a description', (value) =>
          value.getCurrentContent().hasText()
        )
        .test(
          'max length',
          'Description must be less than 1500 characters',
          (value) => value.getCurrentContent().getPlainText().length < 1500
        )
        .required('Description is required.'),
    }),
    onSubmit: (values, { setSubmitting }) => {
      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('pipelines', values.pipeline.value);
      formData.append('status', STATUS_NEW_ID);
      formData.append(
        'description',
        JSON.stringify(convertToRaw(values.description.getCurrentContent()))
      );
      values.attachments.map((attachment) => {
        formData.append('attachments', attachment.file);
      });

      apiPostCreateTicket(formData)
        .then((res) => {
          if (res.status === false) return;
          onClose();
          successToast('Ticket created successfully');
          dispatch(ticketsActions.triggerRefetch());
          dispatch(ticketsActions.resetOpenedTicket());
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  useEffect(() => {
    if(!ticketData) return;
    formik.setFieldValue("pipeline", ticketData.pipeline)
    formik.setFieldValue("title", ticketData.title)
  }, [ticketData])


  const handleFilesUpload = (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    if (acceptedFiles.length + formik.values.attachments.length > 5) {
      errorToast(`Cannot upload more than 5 images`);
      return;
    }

    const newAttachments = [];
    Array.from(acceptedFiles).forEach((file) => {
      const size = file.size / 1024 / 1024;
      if (size > 5) {
        errorToast(`File ${file.name} is too big. Max size is 5MB`);
        return;
      }
      newAttachments.push({
        id: generateUUID(),
        src: URL.createObjectURL(file),
        file,
      });
    });

    formik.setFieldValue('attachments', [...formik.values.attachments, ...newAttachments]);
  };

  const handleFileRemove = (id) => {
    const removedAttachment = formik.values.attachments.find((attachment) => attachment.id === id);
    URL.revokeObjectURL(removedAttachment.src);

    const newAttachments = formik.values.attachments.filter((attachment) => attachment.id !== id);
    formik.setFieldValue('attachments', newAttachments);
  };

  const onClose = () => {
    formik.resetForm();
    dispatch(createTicketModalActions.close());
  };

  const getPipelineOptions = () => {
    apiGetSupportPipelines().then((res) => {
      let data = res.results.map((item) => ({ value: item.id, label: item.name }));
      setPipelineOptions(data);
    });
  };

  useEffect(() => {
    if (currentUser && currentUserId !== currentUser.id) getPipelineOptions();

    return () => dispatch(createTicketModalActions.close());
  }, [currentUser, currentUserId]);

  useCustomKeyPress({ callback: formik.handleSubmit, active: open && !formik.isSubmitting });

  return (
    <Modal show={open} size="lg" className="create-ticket" backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title as="h4" className="fw-normal">
          Create Ticket
        </Modal.Title>
        <Button onClick={onClose} className="modalCloseButton lf-modal-close-button">
          <CloseIconSmall />
        </Button>
      </Modal.Header>
      <Form
        onSubmit={formik.handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Modal.Body>
          <Form.Group className="mb-4">
            {formik.errors.pipeline && formik.touched.pipeline ? (
              <Form.Label className="text-danger">
                <ExclamationIcon /> <span>{formik.errors.pipeline}</span>
              </Form.Label>
            ) : (
              <Form.Label>Category</Form.Label>
            )}
            <StyledSelect
              placeholder="Choose category"
              value={formik.values.pipeline}
              options={pipelineOptions}
              onChange={(selectedOption) => {
                let event = { target: { name: 'pipeline', value: selectedOption } };
                formik.handleChange(event);
              }}
              onBlur={() => {
                formik.handleBlur({ target: { name: 'pipeline' } });
              }}
            />
          </Form.Group>
          <Form.Group className="mb-4">
            {formik.errors.title && formik.touched.title ? (
              <Form.Label className="text-danger">
                <ExclamationIcon /> <span>{formik.errors.title}</span>
              </Form.Label>
            ) : (
              <Form.Label>Title</Form.Label>
            )}
            <Form.Control
              type="text"
              name="title"
              placeholder="Add title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Form.Group>
          <Form.Group className="mb-3 flex d-flex flex-column">
            {formik.errors.description && formik.touched.description ? (
              <Form.Label className="text-danger">
                <ExclamationIcon /> <span>{formik.errors.description}</span>
              </Form.Label>
            ) : (
              <Form.Label>Message</Form.Label>
            )}
            <RichEditor
              placeholder="Description"
              editorState={formik.values.description}
              onEditorStateChange={(val) => formik.setFieldValue('description', val)}
              onEditorBlur={() => formik.setFieldTouched('description', true)}
              attachments={formik.values.attachments}
              onAttachmentsChange={handleFilesUpload}
              onAttachmentRemove={handleFileRemove}
            />
          </Form.Group>
          <div>
            <BigButton
              variant="warning"
              size="sm"
              buttonTitle="Send"
              type="submit"
              disabled={formik.isSubmitting}
              isLoading={formik.isSubmitting}
              style={{
                padding: '4px 16px',
                fontSize: '16px',
                height: '32px',
              }}
            />
          </div>
        </Modal.Body>
      </Form>
    </Modal>
  );
};

export default CreateTicketModal;
